import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Swal from 'sweetalert2';
import headerimage from '../../assets/images/ascas.svg';
import SinglePatientInfo from '../../Components/Global_Components/common/SinglePatientInfo';
import TemplateTable from '../../Components/Global_Components/TemplateTable/TemplateTable';
import Headers from '../../Components/Header/Headers';
import {PrescriptionFooter} from '../../Components/Prescription Footer/PrescriptionFooter';
import {postPrescriptionTemplate} from '../../Redux/Thunk/Actions';
import {handleNill, renderDynamicTable, renderReportfield} from '../../utils/templateUtils';
import './artSummary.scss';

export default function ArtSummary(props) {
  const [detailsRows, setDetailsRows] = useState([
    {
      parameter: 'AFC (Antral Follicle Count)',
      details: ''
    },
    {
      parameter: 'Injection Protocol',
      details: ''
    },
    {
      parameter: 'Drugs used',
      details: ''
    },
    {
      parameter: 'Day of Stimulation Started',
      details: ''
    },
    {
      parameter: 'Days of stimulation',
      details: ''
    },
    {
      parameter: 'E2 (Estradiol) at Trigger',
      details: ''
    },
    {
      parameter: 'Trigger Used',
      details: ''
    },
    {
      parameter: 'Endometrial Thickness at Trigger',
      details: ''
    },
    {
      parameter: 'Trial Cannulation at OPU',
      details: ''
    },
    {
      parameter: 'SSG (Cavity Assessment)',
      details: ''
    },
    {
      parameter: 'Sperm Selection Method',
      details: ''
    }
  ]);

  const detailsHeaders = [
    {key: 'parameter', label: 'PARAMETER', inputType: 'text', readOnly: true},
    {
      key: 'details',
      label: 'DETAILS',
      inputType: 'conditional',
      conditions: {
        'Trial Cannulation at OPU': {
          type: 'select',
          options: ['Easy', 'Difficult']
        },
        'Drugs used': {
          type: 'select',
          options: ['HMG', 'HMG+RFSH', 'RFSH']
        },
        'Sperm Selection Method': {
          type: 'select',
          options: ['MF', 'PICSI', 'Regular']
        }
      }
    }
  ];

  const [oocyteRows, setOocyteRows] = useState([
    {
      parameter: 'No. of Eggs Retrieved',
      count: ''
    },
    {
      parameter: 'No. of Matured (MII) Eggs',
      count: ''
    },
    {
      parameter: 'Oocyte quality',
      count: ''
    },
    {
      parameter: 'No. Fertilized',
      count: ''
    },
    {
      parameter: 'Sperm quality',
      count: ''
    },
    {
      parameter: 'Embryos on Day 3',
      count: ''
    },
    {
      parameter: 'Embryos on Day 5',
      count: ''
    },
    {
      parameter: 'Embryos on Day 6',
      count: ''
    }
  ]);

  const oocyteHeaders = [
    {key: 'parameter', label: 'PARAMETER', inputType: 'text', readOnly: true},
    {key: 'count', label: 'COUNT'}
  ];

  const [medicationRows, setMedicationRows] = useState([
    {
      sno: '1',
      drug: 'Inj. Lupride Depot / Inj. LUGONIST 3.75 mg (Leuprolide acetate depot)',
      indication: 'GnRH agonist for down-regulation / endometrial preparation',
      dose: '3.75 mg',
      frequency: 'STAT',
      route: 'IM / SC',
      timing: 'Single dose',
      duration: ''
    },
    {
      sno: '2',
      drug: 'T. Doxycycline 100 mg (Doxycycline hydrochloride)',
      indication: 'Broad-spectrum antibiotic',
      dose: '1 tab',
      frequency: '1-0-1',
      route: 'Oral',
      timing: 'After food',
      duration: '21 days'
    },
    {
      sno: '3',
      drug: 'T. Metrogyl 400 mg (Metronidazole)',
      indication: 'Anaerobic coverage / endometrial optimization',
      dose: '1 tab',
      frequency: '1-1-1',
      route: 'Oral',
      timing: 'After food',
      duration: '21 days'
    },
    {
      sno: '4',
      drug: 'T. Pan 40 mg (Pantoprazole 40 mg)',
      indication: 'Gastric protection',
      dose: '1 tab',
      frequency: '1-0-0',
      route: 'Oral',
      timing: 'Before food',
      duration: '21 days'
    },
    {
      sno: '5',
      drug: 'Ecotas BV / Ultrafem Probiotics',
      indication: 'Vaginal flora restoration / discharge management',
      dose: '1 tab',
      frequency: 'twice daily',
      route: 'Oral',
      timing: '',
      duration: '30 days'
    },
    {
      sno: '6',
      drug: 'T. Nortas CR 10 mg',
      indication: '',
      dose: '1 tab',
      frequency: 'twice daily',
      route: 'Oral',
      timing: '',
      duration: '15 days'
    },
    {
      sno: '7',
      drug: 'T. Progynova 2 mg',
      indication: '',
      dose: '1 tab',
      frequency: 'twice daily',
      route: 'Oral',
      timing: '',
      duration: '21 days'
    },
    {
      sno: '8',
      drug: 'T. Meparate 10 mg',
      indication: '',
      dose: '1 tab',
      frequency: 'twice daily',
      route: 'Oral',
      timing: '',
      duration: '21 days'
    },
    {
      sno: '9',
      drug: 'T. C Vit / Celin',
      indication: 'Antioxidant support',
      dose: '500 mg',
      frequency: '1-0-1',
      route: 'Oral',
      timing: 'After food',
      duration: '30 days'
    },
    {
      sno: '10',
      drug: 'T. Neuroplex / Neurobion',
      indication: 'B-complex support',
      dose: '1 tab',
      frequency: 'OD',
      route: 'Oral',
      timing: 'After food',
      duration: '30 days'
    }
  ]);

  const medicationHeaders = [
    {key: 'sno', label: 'S.No', inputType: 'text', readOnly: true},
    {key: 'drug', label: 'Drug (Brand + Generic Name)', inputType: 'text'},
    {key: 'indication', label: 'Indication / Instructions', inputType: 'text'},
    {key: 'dose', label: 'Dose', inputType: 'text'},
    {key: 'frequency', label: 'Frequency', inputType: 'text'},
    {key: 'route', label: 'Route', inputType: 'text'},
    {key: 'timing', label: 'Timing', inputType: 'text'},
    {key: 'duration', label: 'Duration', inputType: 'text'}
  ];

  const [additionalInfoRows, setAdditionalInfoRows] = useState([
    {
      parameter: 'Any Procedures Done',
      details: ''
    },
    {
      parameter: 'Endometrial Preparation Technique',
      details: ''
    },
    {
      parameter: 'Remarks / Notes',
      details: ''
    }
  ]);

  const additionalInfoHeaders = [
    {key: 'parameter', label: 'PARAMETER', inputType: 'text', readOnly: true},
    {
      key: 'details',
      label: 'DETAILS',
      inputType: 'conditional',
      conditions: {
        'Any Procedures Done': {
          type: 'select',
          options: ['Hysteroscopy', 'Laparoscopy', 'Cavity Scan']
        },
        'Endometrial Preparation Technique': {
          type: 'select',
          options: ['DR-FET', 'SC-FET', 'Natural', 'HRT']
        }
      }
    }
  ];

  const [showTables, setShowTables] = useState({
    details: true,
    oocyte: true,
    medication: true,
    additionalInfo: true
  });

  const [formData, setFormData] = useState({
    advice: '',
    nextAppointment: ''
  });
  const [show, setshow] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patientId = useSelector(state => state.counter.patientid);

  const handleChange = e => {
    const {name, value} = e.target;
    setFormData({...formData, [name]: value});
  };

  const handlesubmit = () => {
    try {
      if (props.patientsData.id !== '' && props.patientsData.p_uhid !== '' && /^(ASCAS|VDASCAS)\d+$/i.test((props.patientsData.p_uhid || '').trim())) {
        setshow(true);
        setTimeout(async () => {
          const htmlPayload = document.getElementById('artSummaryReportWrap').innerHTML;

          const payload = {
            payload: {
              prescription_template: htmlPayload,
              patient_id: patientId,
              description: 'ART Summary',
              template_type: 'ART Summary'
            }
          };

          props.loaderchange(true);
          const patentDetails = postPrescriptionTemplate(payload);
          const response = await dispatch(patentDetails);
          if (response?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/PatientView');
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
        }, 3000);
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  return (
    <div id='ArtSummaryTemplate'>
      <div className='ArtSummaryTemplate'>
        <div className='' style={{width: '100%', display: 'block'}} id='patientInfo1'>
          <div style={{width: '100%'}}>
            <img
              className='header_image'
              src={headerimage}
              style={{
                width: '100%',
                margin: '0 auto'
              }}></img>
          </div>
          <h2 style={{display: 'flex', justifyContent: 'center'}}>ASCAS - ART SUMMARY AND DETAILS</h2>

          <SinglePatientInfo patientsData={props.patientsData} handleChange={handleChange} />
        </div>
        <div className='detail'>
          {showTables.details && (
            <TemplateTable
              id='details'
              title=''
              headers={detailsHeaders}
              rows={detailsRows}
              onRowsChange={(id, newRows) => setDetailsRows(newRows)}
              enableActions={true}
              onNill={() => handleNill('details', setShowTables)}
            />
          )}
          {showTables.oocyte && (
            <TemplateTable
              id='oocyte'
              title='OOCYTE AND EMBRYYO DEVELOPMENT'
              headers={oocyteHeaders}
              rows={oocyteRows}
              onRowsChange={(id, newRows) => setOocyteRows(newRows)}
              enableActions={true}
              onNill={() => handleNill('oocyte', setShowTables)}
            />
          )}
          {showTables.medication && (
            <TemplateTable
              id='medication'
              title='MEDICATIONS'
              headers={medicationHeaders}
              rows={medicationRows}
              onRowsChange={(id, newRows) => setMedicationRows(newRows)}
              enableActions={true}
              onNill={() => handleNill('medication', setShowTables)}
            />
          )}
          {showTables.additionalInfo && (
            <TemplateTable
              id='additionalInfo'
              title='ADDITIONAL INFORMATION'
              headers={additionalInfoHeaders}
              rows={additionalInfoRows}
              onRowsChange={(id, newRows) => setAdditionalInfoRows(newRows)}
              enableActions={true}
              onNill={() => handleNill('additionalInfo', setShowTables)}
            />
          )}
        </div>

        <div className='d-flex align-items-start py-2'>
          <label htmlFor='advice' className='fw-bold' style={{width: '180px'}}>
            ADVICE:
          </label>
          <textarea className='Textarea' placeholder='Advice' id='advice' name='advice' onChange={handleChange} rows='3' cols='100' />
        </div>

        <p className='pt-3' style={{fontWeight: '600'}}>
          NEXT Appointment - <input name='nextAppointment' onChange={handleChange} type='date' className={`createTime ${show ? 'hide' : 'show'}`} />
        </p>

        <div className={`Footer ${show ? 'hide' : 'show'}`}>
          <button onClick={handlesubmit} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
      <div id='artSummaryReport'>
        <div id='artSummaryReportWrap'>
          <Headers />
          <p className='Dani'></p>
          <div
            className='patient'
            style={{
              border: '1px solid black',
              borderRadius: '10px',
              marginBottom: '5px'
            }}>
            <div className='nameToAgeContainer' style={{display: 'flex', marginBottom: '5px'}}>
              {renderReportfield('Patient Name :', `${props.patientsData.titles} ${props.patientsData.first_name} ${props.patientsData.last_name}`)}
              {renderReportfield('Patient ID :', props.patientsData.p_uhid)}
              {renderReportfield('Gender :', props.patientsData.gender)}
              {renderReportfield('Age :', props.patientsData.age)}
            </div>
          </div>
          <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', marginTop: '5px'}}>
            <h4 style={{fontWeight: 600, color: '#333', marginBottom: '15px'}}>ASCAS - ART SUMMARY AND DETAILS</h4>
          </div>

          {showTables.details && renderDynamicTable('', ['PARAMETER', 'DETAILS'], ['parameter', 'details'], detailsRows)}

          {showTables.oocyte && renderDynamicTable('OOCYTE AND EMBRYO DEVELOPMENT', ['PARAMETER', 'COUNT'], ['parameter', 'count'], oocyteRows)}

          {showTables.medication &&
            renderDynamicTable(
              'MEDICATIONS',
              ['S.No', 'Drug (Brand + Generic Name)', 'Indication / Instructions', 'Dose', 'Frequency', 'Route', 'Timing', 'Duration'],
              ['sno', 'drug', 'indication', 'dose', 'frequency', 'route', 'timing', 'duration'],
              medicationRows
            )}

          {showTables.additionalInfo && renderDynamicTable('ADDITIONAL INFORMATION', ['PARAMETER', 'DETAILS'], ['parameter', 'details'], additionalInfoRows)}

          <div style={{marginBottom: '5px'}}>
            <div style={{fontWeight: '600', margin: '0 10px 10px 10px'}}>ADVICE:</div>
            <pre style={{margin: '0 10px 10px 10px', fontFamily: 'inherit', whiteSpace: 'pre-wrap'}}>{formData.advice}</pre>
          </div>

          <div style={{marginTop: '20px', borderBottom: '1px solid #ddd', paddingBottom: '15px'}}>
            <div style={{marginTop: '5px'}}>{renderReportfield('Next Appointment : ', formData.nextAppointment?.split('-').reverse().join('-'))}</div>
          </div>
          <p className='Dani'></p>
          <PrescriptionFooter />
        </div>
      </div>
    </div>
  );
}
